import { compareQuickNotes, type QuickNoteDto } from '@fos/shared/contracts/quick-notes';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { trpc } from '../../lib/trpc';

/**
 * Dane sekcji „Szybkie notatki” na Dziś (karteczki, DECISIONS 2026-10-17 [P]): `quickNotes.list({ state: 'open' })`
 * z tRPC (packages/core/src/modules/notes — tylko notatki osoby z sesji). Klucz pod `['today', …]`, więc odświeżenie
 * po działaniach Copilota (`useCopilotActionsInvalidate` unieważnia `['today']`) obejmuje też karteczki.
 */
export const QUICK_NOTES_QUERY_KEY = ['today', 'quick-notes'] as const;

export function useQuickNotes() {
  const utils = trpc.useUtils();
  return useQuery({
    queryKey: QUICK_NOTES_QUERY_KEY,
    queryFn: () => utils.client.quickNotes.list.query({ state: 'open' }),
    staleTime: 30_000,
  });
}

/**
 * Zmiany listy w pamięci podręcznej (optymistycznie: odhaczenie znika od razu). Nowa i zmieniona karteczka staje tam,
 * gdzie postawi ją serwer (`compareQuickNotes`: z terminem najpierw, potem najnowsze) — po przeładowaniu nie skacze.
 */
export function useQuickNotesCache() {
  const queryClient = useQueryClient();
  const get = () => queryClient.getQueryData<QuickNoteDto[]>(QUICK_NOTES_QUERY_KEY) ?? [];
  const set = (next: QuickNoteDto[]) => queryClient.setQueryData(QUICK_NOTES_QUERY_KEY, next);
  // Wczytanie listy w toku (np. pierwsze na Dziś — partia z wolniejszymi zapytaniami) zwróciłoby stan sprzed zmiany
  // i nadpisało ją w pamięci podręcznej (nowa karteczka znikała). Wtedy listę trzeba pobrać jeszcze raz.
  const settle = () => {
    if (queryClient.isFetching({ queryKey: QUICK_NOTES_QUERY_KEY }) > 0) {
      void queryClient.invalidateQueries({ queryKey: QUICK_NOTES_QUERY_KEY });
    }
  };
  return {
    get,
    set,
    settle,
    upsert(note: QuickNoteDto) {
      const list = get();
      const next = list.some((n) => n.id === note.id)
        ? list.map((n) => (n.id === note.id ? note : n))
        : [note, ...list];
      set([...next].sort(compareQuickNotes));
      settle();
    },
    remove(id: string) {
      set(get().filter((n) => n.id !== id));
    },
    refresh: () => queryClient.invalidateQueries({ queryKey: QUICK_NOTES_QUERY_KEY }),
  };
}
